import { useQuery } from '@tanstack/react-query';
import {
  CareHistory, getAppointmentHistoryByPatientIds, getCareHistoryByPatientIds,
  getReferralByAboutUserIds, getReferralByStudentId, getStudentByStudentId, getStudentContacts,
  getStudentsByOrgId, Student, StudentReferral
} from '@uplift/api';

import { BaseQueryOpts } from '../baseTypes';
import { higherEdQueryKeys } from './higherEdQueryKeys';
import { useCurrentuser } from '@uplift/contexts';
import { useHigherEdOrgQuery } from './higherEdOrg.queries';
import { SubSystem } from '@uplift/types';

export type StudentTable = Student & {
  activeCareRelations: CareHistory['activeCareRelations'];
  appointmentHistory: number;
  careType: StudentReferral['careType'];
  referralCreatedAt: StudentReferral['createdAt'];
  patientAttendedAppointment: boolean;
};

export const useGetStudentByStudentId = (studentId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: higherEdQueryKeys.student(studentId),
    queryFn: async () => {
      const response = await getStudentByStudentId(studentId);

      return response.data;
    },
    ...opts,
  });
};

export const useGetStudentsByOrgIdQuery = (orgId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: higherEdQueryKeys.students(orgId),
    queryFn: async () => {
      const { data } = await getStudentsByOrgId(orgId);
      const ids = data.map(student => student.user.id);
      const { data: { students } } = await getCareHistoryByPatientIds(ids);
      const { data: appointmentHistory } = await getAppointmentHistoryByPatientIds(ids);
      const { data: referrals } = await getReferralByAboutUserIds(ids);

      const studentsTableData: StudentTable[] = await Promise.all(data.map(async (student: StudentTable) => {
        student.user = { ...student.user, ...students[student.user.id].patient };
        student.activeCareRelations = students[student.user.id].activeCareRelations;
        student.patientAttendedAppointment = students[student.user.id].patientAttendedAppointment;
        student.appointmentHistory = appointmentHistory[student.user.id];
        student.careType = referrals[student.user.id] ? referrals[student.user.id][0]?.careType : null;
        student.referralCreatedAt = referrals[student.user.id] ? referrals[student.user.id][0]?.createdAt : null;

        return student;
      }));

      return studentsTableData;
    },
    ...opts,
  });
};

export const useReferralByStudentIdQuery = (studentId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: higherEdQueryKeys.studentReferral(studentId),
    queryFn: async() => {
      const response = await getReferralByStudentId(studentId);

      return response.data;
    },
    ...opts,
  });
};

export const useReferralByUserIdsQuery = (ids: string[], opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: higherEdQueryKeys.bulkStudentReferrals(),
    queryFn: async() => {
      const response = await getReferralByAboutUserIds(ids);

      return response.data;
    },
    ...opts,
  });
};

export const useStudentContactsQuery = (studentId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: higherEdQueryKeys.studentContacts(studentId),
    queryFn: async () => {
      const contactsResponse = await getStudentContacts(studentId);

      return contactsResponse.data;
    },
    staleTime: Infinity,
    ...opts,
  });
};


export const useStudentDetails = (userId: string, opts?: BaseQueryOpts) => {
  const { data: student, isInitialLoading: studentLoading } = useGetStudentByStudentId(userId, opts);
  const { data: higherEdOrg, isInitialLoading: orgLoading } = useHigherEdOrgQuery(student?.orgId ?? '', { enabled: !!student?.orgId, ...opts });

  return {
    student,
    higherEdOrg,
    isLoading: studentLoading || orgLoading,
  };
};

export const useCurrentStudentDetails = (opts?: BaseQueryOpts) => {
  const me = useCurrentuser();

  return useStudentDetails(me.id, { ...opts, enabled: me.subsystem === SubSystem.higherEd });
};
